import { z } from 'zod';
export const SessionStatusSchema = z.enum([
    // 'pending' is the pre-flight state for the pay-as-you-go path:
    // driver-start created the row, the Stripe pre-auth landed, the
    // OCPP RemoteStartTransaction was Accepted — but the charger
    // hasn't fired StartTransaction yet (driver hasn't plugged in).
    // Promotes to 'active' when StartTransaction lands. The 4b
    // sweeper marks abandoned 'pending' rows as 'failed' (with the
    // PI cancelled) after PENDING_SESSION_TIMEOUT_MINUTES.
    'pending',
    // 'sweeping' is the transitional claim state used by the orphan-
    // pending sweeper to prevent two API instances from cancelling
    // the same Stripe pre-auth concurrently. It exists for the
    // duration of one Stripe cancel call (milliseconds typically).
    // Rows stuck in 'sweeping' for more than 1 minute indicate a
    // crashed sweeper and are re-claimed by the next sweep run.
    'sweeping',
    'active', 'completed', 'failed', 'stopped_by_user', 'stopped_by_system',
]);
export const MeterValueSchema = z.object({
    timestamp: z.coerce.date(),
    energy_kwh: z.number().nonnegative(),
    power_kw: z.number().nonnegative(),
    soc_percent: z.number().min(0).max(100).optional(),
    voltage: z.number().optional(),
    current: z.number().optional(),
});
export const SessionCostSchema = z.object({
    energy_cost: z.number().nonnegative(),
    time_cost: z.number().nonnegative(),
    flat_fee: z.number().nonnegative(),
    tax: z.number().nonnegative(),
    total: z.number().nonnegative(),
    currency: z.string().length(3),
});
// Third-party (guest) access — session-side. 'tenant' = charger
// tenant matches user tenant; 'guest' = cross-tenant access. Default
// 'tenant' makes legacy serializations round-trip and matches the
// migration backfill semantic.
export const SessionAccessModeSchema = z.enum(['tenant', 'guest']);
export const SessionSchema = z.object({
    _id: z.string(),
    tenant_id: z.string(),
    charger_id: z.string(),
    connector_id: z.number().int().positive(),
    location_id: z.string(),
    user_id: z.string().optional(),
    rfid_tag: z.string().optional(),
    transaction_id: z.string(),
    status: SessionStatusSchema,
    started_at: z.coerce.date(),
    ended_at: z.coerce.date().optional(),
    energy_kwh: z.number().nonnegative(),
    duration_minutes: z.number().nonnegative(),
    meter_start: z.number().nonnegative(),
    meter_stop: z.number().optional(),
    meter_values: z.array(MeterValueSchema),
    cost: SessionCostSchema,
    tariff_id: z.string(),
    stripe_payment_intent_id: z.string().optional(),
    carbon_offset_kg: z.number().optional(),
    // Third-party (guest) access.
    access_mode: SessionAccessModeSchema.default('tenant'),
    guest_markup_bps: z.number().int().min(0).max(50_000).nullable().default(null),
    // Building electricity reimbursement rate (cents/kWh) denormalized
    // at session-start time. Null on legacy sessions; StopTransaction
    // falls back to Location.electricity_rate_cents for those. Not whole
    // cents: the location's rate is kept to a hundredth of a cent, 43.63
    // (Warren, 21 October 2026, night), and pinned as it is.
    electricity_rate_at_start_cents: z.number().min(0).nullable().optional(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
//# sourceMappingURL=session.js.map